import { DeleteIcon, PlusIcon, TickIcon } from "../../../../atoms/CustomIcons";
import { useState, useMemo } from "react";
import {
  Button,
  Card,
  Dropdown,
  Flex,
  Image,
  Popover,
  Progress,
  Tooltip,
} from "antd";

import { useAppDispatch } from "../../../../config/hooks";
import { getPotentialDealDetails } from "../../ducks/actions";
import { useNavigate } from "react-router-dom";
import { MoreOutlined } from "@ant-design/icons";
import ConfirmDelete from "../../../../atoms/ConfirmDelete";
import { AxiosError } from "axios";
import ErrorMessage from "../../../../atoms/Toasts/ErrorMessage";
import SuccessMessage from "../../../../atoms/Toasts/SuccessMessage";
import { changeProposalStatus, deleteProposalById } from "../../ducks/services";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import PendingApproval from "../CardStatus/PendingApproval";
import InProgress from "../CardStatus/InProgress";
import Ready from "../CardStatus/Ready";
import Sent from "../CardStatus/Sent";
import Accepted from "../CardStatus/Accepted";
import Revision from "../CardStatus/Revision";
import Declined from "../CardStatus/Declined";
import Archived from "../CardStatus/Archived";
import { formatRelativeTime } from "../../../../atoms/TimeFormatDisplay";
import { PopUp } from "../../../../atoms/PopUp";
import { GetDealType } from "../../ducks/types";

dayjs.extend(isToday);
dayjs.extend(isYesterday);

interface Proposal {
  id: string;
  name: string; // This will be the original name, potentially the date string
  summary?: string;
  modified_date: string;
  gross_margin_perc: number;
  total_amount: number;
  status: string;
}

interface FormattedProposal extends Proposal {
  display_name: string;
}

interface TableRecordProps {
  name: string;
  display_name: string;
  id: string;
  status: string;
}

interface PropsType {
  setLoading: (loading: boolean) => void;
  loading: boolean;
  dealsDetailData: GetDealType | null;
}

const formatProposalsForDisplay = (
  proposals: Proposal[]
): FormattedProposal[] => {
  if (!proposals || proposals.length === 0) {
    return [];
  }

  const proposalsByDate = new Map<string, Proposal[]>(); // Map<"YYYY-MM-DD", Proposal[]>

  proposals.forEach((proposal) => {
    const dateKey = dayjs(proposal?.name).format("YYYY-MM-DD");
    if (!proposalsByDate.has(dateKey)) {
      proposalsByDate.set(dateKey, []);
    }
    proposalsByDate.get(dateKey)?.push(proposal);
  });

  const formattedProposals: FormattedProposal[] = [];

  proposalsByDate?.forEach((dailyProposals, dateKey) => {
    dailyProposals?.sort((a, b) =>
      dayjs(a.modified_date).diff(dayjs(b.modified_date))
    );

    if (dailyProposals?.length === 1) {
      formattedProposals?.push({
        ...dailyProposals?.[0],
        display_name: `Proposal - ${dayjs(dailyProposals?.[0].name).format(
          "MM.DD.YYYY"
        )}`,
      });
    } else {
      dailyProposals?.forEach((proposal, index) => {
        const formattedDate = dayjs(dateKey).format("MM.DD.YYYY");
        const versionName = `Proposal v.${index + 1} - ${formattedDate}`;
        formattedProposals.push({
          ...proposal,
          display_name: versionName,
        });
      });
    }
  });

  return formattedProposals.sort((a, b) =>
    dayjs(a.modified_date).diff(dayjs(b.modified_date))
  );
};

export default function ProposalCards(props: PropsType) {
  const { setLoading, loading, dealsDetailData } = props;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tableRecord, setTableRecord] = useState<TableRecordProps | null>(null);
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const dealId = pathSegments[pathSegments.length - 2];

  const handleStatusChange = (status: string) => {
    setLoading(true);
    if (tableRecord) {
      const payload = {
        status,
        deal_id: dealId,
      };
      changeProposalStatus(payload, tableRecord?.id)
        .then(() => {
          setLoading(false);
          dispatch(getPotentialDealDetails(dealId));
          setTableRecord(null);
          SuccessMessage("Status changed");
        })
        .catch((e: unknown) => {
          if (e instanceof AxiosError) {
            setLoading(false);
            ErrorMessage(
              e?.response?.data?.error?.message ?? "Something went wrong"
            );
          } else {
            setLoading(false);
            ErrorMessage("Something went wrong");
          }
        });
    }
  };

  const formattedProposals = useMemo(() => {
    return formatProposalsForDisplay(dealsDetailData?.proposals || []);
  }, [dealsDetailData?.proposals]);

  const items = [
    {
      key: "1",
      label: (
        <Button type="text" className="no-hover">
          <Image
            src="/images/potential-deals/make-copy.svg"
            alt="Change"
            preview={false}
          />{" "}
          Make a copy
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Popover
          arrow={false}
          overlayClassName="status_popover"
          content={
            <ul>
              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => handleStatusChange("in_progress")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #6454F4",
                      background: "#6454F4",
                    }}
                  />{" "}
                  In progress
                  {tableRecord?.status === "in_progress" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>

              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => handleStatusChange("approval_pending")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #943AF5",
                      background: "#943AF5",
                    }}
                  />{" "}
                  Pending approval
                  {tableRecord?.status === "approval_pending" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>
              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => handleStatusChange("ready")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #44A6F1",
                      background: "#44A6F1",
                    }}
                  />{" "}
                  Ready
                  {tableRecord?.status === "ready" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>
              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => handleStatusChange("sent")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #E6C300",
                      background: "#E6C300",
                    }}
                  />{" "}
                  Sent
                  {tableRecord?.status === "sent" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>
              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => handleStatusChange("accepted")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #00A07D",
                      background: "#00A07D",
                    }}
                  />{" "}
                  Accepted
                  {tableRecord?.status === "accepted" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>
              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => handleStatusChange("revision")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #F06400",
                      background: "#F06400",
                    }}
                  />{" "}
                  Revision
                  {tableRecord?.status === "revision" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>
              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => handleStatusChange("declined")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #CD1951",
                      background: "#CD1951",
                    }}
                  />{" "}
                  Declined
                  {tableRecord?.status === "declined" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>
              <li>
                <Button
                  type="text"
                  className="color_popover"
                  onClick={() => handleStatusChange("archived")}
                >
                  <span
                    className="badge"
                    style={{
                      border: "1px solid #98A3A9",
                      background: "#98A3A9",
                    }}
                  />{" "}
                  Archived
                  {tableRecord?.status === "archived" && (
                    <TickIcon
                      style={{ color: "#00A07D", marginLeft: "auto" }}
                    />
                  )}
                </Button>
              </li>
            </ul>
          }
          placement="rightTop"
          getPopupContainer={(trigger) =>
            trigger.parentElement || document.body
          }
        >
          <Button type="text" className="no-hover">
            <Image
              src="/images/potential-deals/change-status.svg"
              alt="Move crew’s schedule"
              preview={false}
            />{" "}
            Change status to ...
          </Button>
        </Popover>
      ),
    },
    {
      key: "3",
      label: (
        <Button type="text" className="no-hover">
          <Image
            src="/images/potential-deals/export-icon.svg"
            alt="Export"
            preview={false}
          />{" "}
          Export takeoff measurements
        </Button>
      ),
    },
    {
      key: "4",
      label: (
        <Button type="text" className="no-hover">
          <Image
            src="/images/potential-deals/export-icon.svg"
            alt="Export"
            preview={false}
          />{" "}
          Export materials list
        </Button>
      ),
    },
    {
      key: "5",
      label: (
        <Button
          type="text"
          className="delete-hover"
          onClick={() => {
            setIsDeleteModalOpen(true);
          }}
          icon={<DeleteIcon />}
        >
          Delete
        </Button>
      ),
    },
  ];

  const deleteFunction = () => {
    setLoading(true);
    deleteProposalById(tableRecord?.id as string)
      .then(() => {
        setLoading(false);
        setIsDeleteModalOpen(false);
        setTableRecord(null);
        dispatch(getPotentialDealDetails(dealId));
        SuccessMessage("Proposal deleted");
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const deleteProposalPopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Are you sure you want to delete <b>{tableRecord?.display_name}</b>?
            <br />
            This action cannot be undone.
          </>
        }
        btnText="Delete proposal"
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const formatRelativeDateTime = (dateInput: string | null): string => {
    if (!dateInput) {
      return ""; // Return empty string or a default like 'N/A' if no date is provided
    }

    const date = dayjs(dateInput); // Convert input to a dayjs object

    // Format the time part (e.g., "10:50 AM")
    const timeFormat = "h:mm A"; // 12-hour format with AM/PM

    if (date.isYesterday()) {
      return `Y'day at ${date.format(timeFormat)}`;
    } else if (date.isToday()) {
      return `Today at ${date.format(timeFormat)}`;
    } else {
      // For any other date (future or older past dates)
      return date.format("MMM DD, YYYY [at] h:mm A"); // e.g., "Jul 14, 2025 at 10:50 AM"
    }
  };

  return (
    <>
      {formattedProposals && formattedProposals?.length > 0 ? (
        formattedProposals.map((proposal) => (
          <Card className="proposal_card">
            <div className="proposal_card_header">
              <h4
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                {proposal?.display_name}
                {proposal?.summary && (
                  <Tooltip title={proposal?.summary}>
                    <img
                      src="/images/potential-deals/proposal_temp_icon.svg"
                      alt=""
                    />
                  </Tooltip>
                )}
              </h4>
              <Dropdown
                menu={{ items }}
                trigger={["click"]}
                placement="bottomRight"
                className="actionButton"
              >
                <Button
                  onClick={(x) => {
                    x.stopPropagation();
                    setTableRecord(proposal);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{
                    padding: "0px",
                    height: "28px",
                    width: "28px",
                    backgroundColor: "transparent",
                    border: "1px solid #DEE5E6",
                    borderRadius: "8px",
                  }}
                  className="only_icon"
                  icon={<MoreOutlined />}
                />
              </Dropdown>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Card
                className="estimate"
                onClick={() =>
                  navigate(
                    `/edit-estimate/${proposal?.id}/${dealId}/${companyId}`
                  )
                }
                style={{
                  cursor: "pointer",
                }}
              >
                <h5 style={{ marginBottom: "5px" }}>
                  Estimate
                  <span
                    style={{
                      fontSize: "12px",
                      lineHeight: "16px",
                      fontWeight: "400",
                      color: "#474C4D",
                      marginLeft: "8px",
                    }}
                  >
                    Last edit: {formatRelativeTime(proposal?.modified_date)}
                  </span>
                </h5>
                <Flex gap="small" vertical>
                  <Progress
                    strokeColor="#00A07D"
                    percent={proposal?.gross_margin_perc || 0}
                    size={["100%", 12]}
                  />
                </Flex>
                <div
                  style={{
                    marginBottom: "2px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    justifyContent: "space-between",
                  }}
                >
                  <span> Gross margin: </span>
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                    }}
                  >
                    {Number(
                      proposal?.gross_margin_perc?.toFixed(2)
                    ).toLocaleString()}{" "}
                    %
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    justifyContent: "space-between",
                  }}
                >
                  <span> Total amount: </span>
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                    }}
                  >
                    $
                    {Number(
                      proposal?.total_amount?.toFixed(2)
                    ).toLocaleString()}
                  </span>
                </div>
              </Card>
              <div
                className="proposal_document"
                onClick={() =>
                  navigate(`/editor/${proposal?.id}/${dealId}/${companyId}`)
                }
                style={{ cursor: "pointer" }}
              >
                <div className="titleBar">
                  <img
                    src="/images/potential-deals/proposal_document.svg"
                    alt=""
                  />
                </div>

                <h5> Proposal document </h5>
                <h6
                  style={{
                    fontSize: "12px",
                    lineHeight: "16px",
                    fontWeight: "400",
                  }}
                >
                  {" "}
                  Last edit : {formatRelativeDateTime(
                    proposal?.modified_date
                  )}{" "}
                </h6>
              </div>
              {proposal?.status === "in_progress" && <InProgress />}
              {proposal?.status === "approval_pending" && <PendingApproval />}
              {proposal?.status === "ready" && <Ready />}
              {proposal?.status === "sent" && <Sent />}
              {proposal?.status === "accepted" && <Accepted />}
              {proposal?.status === "revision" && <Revision />}
              {proposal?.status === "declined" && <Declined />}
              {proposal?.status === "archived" && <Archived />}
            </div>
          </Card>
        ))
      ) : (
        <Card
          className="empty_proposal"
          style={{ marginTop: "10px", minHeight: "270px" }}
        >
          <img src="/images/potential-deals/empty_proposal.svg" alt="" />
          <h3> Create estimate & proposal </h3>
          <p style={{ marginBottom: "16px" }}>
            Set the scope of work, create an estimate, and send the proposal.
          </p>
          <Button
            type="primary"
            className="outlined-btn"
            icon={<PlusIcon style={{ color: "#151718" }} />}
            onClick={() => navigate(`/estimate/${dealId}/${companyId}`)}
          >
            Create estimate
          </Button>
        </Card>
      )}

      <PopUp {...deleteProposalPopUp} />
    </>
  );
}
